/**
 * Сортировка очередей согласования — таймшитов и отсутствий.
 *
 * Зачем: очереди приходят с сервера в порядке, который руководителю ничего не
 * говорит. Пока строк пять, это терпимо; на «Аналоге» с 26 участниками нужную
 * фамилию приходится искать глазами. Отсюда просьба владелицы 26.08 —
 * «сортировку по дате или фамилии», в обе очереди.
 *
 * Выбор запоминается в localStorage отдельно для каждой очереди: у линейного
 * руководителя и у руководителя проекта разные привычки, и очередь отсутствий
 * не должна перенимать настройку очереди таймшитов.
 */

import { useCallback, useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

/** Как достать из строки значение для сравнения. */
export type SortAccessors<T, K extends string> = Record<K, (item: T) => string | number | null>;

const STORAGE_PREFIX = "skedd.queueSort.";

function readStored<K extends string>(
  storageKey: string,
  allowed: readonly K[],
  fallback: SortState<K>,
): SortState<K> {
  // Приватное окно, отключённые site data, чужая версия формата — везде
  // молча откатываемся к значению по умолчанию: сортировка не то, ради чего
  // стоит ронять экран.
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<SortState<K>>;
    if (
      parsed &&
      typeof parsed.key === "string" &&
      (allowed as readonly string[]).includes(parsed.key) &&
      (parsed.direction === "asc" || parsed.direction === "desc")
    ) {
      return { key: parsed.key as K, direction: parsed.direction };
    }
  } catch {
    /* пустое хранилище или чужой формат — не повод падать */
  }
  return fallback;
}

function isEmpty(value: string | number | null): boolean {
  return value === null || value === "";
}

function compare(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  // Русские фамилии: «Ё» должна стоять рядом с «Е», а не в конце алфавита.
  return String(a).localeCompare(String(b), "ru");
}

export function useQueueSort<T, K extends string>(
  items: T[],
  accessors: SortAccessors<T, K>,
  options: { storageKey: string; initial: SortState<K> },
): {
  sorted: T[];
  sort: SortState<K>;
  /** Клик по колонке: та же — переворот, другая — переключение с её порядком по умолчанию. */
  toggle: (key: K, defaultDirection?: SortDirection) => void;
  setSort: (next: SortState<K>) => void;
} {
  const allowed = useMemo(() => Object.keys(accessors) as K[], [accessors]);
  const [sort, setSortState] = useState<SortState<K>>(() =>
    readStored(options.storageKey, allowed, options.initial),
  );

  const setSort = useCallback(
    (next: SortState<K>) => {
      setSortState(next);
      try {
        localStorage.setItem(STORAGE_PREFIX + options.storageKey, JSON.stringify(next));
      } catch {
        /* не сохранилось — порядок просто не переживёт перезагрузку */
      }
    },
    [options.storageKey],
  );

  const toggle = useCallback(
    (key: K, defaultDirection: SortDirection = "asc") => {
      setSort(
        sort.key === key
          ? { key, direction: sort.direction === "asc" ? "desc" : "asc" }
          : { key, direction: defaultDirection },
      );
    },
    [sort, setSort],
  );

  const sorted = useMemo(() => {
    const accessor = accessors[sort.key];
    if (!accessor) return items;
    // Копия: исходный массив — состояние компонента, править его на месте нельзя.
    const copy = [...items];
    copy.sort((a, b) => {
      const left = accessor(a);
      const right = accessor(b);
      // Пустое значение всегда внизу — и при прямом порядке, и при обратном.
      // Поэтому проверка стоит ДО инверсии: «ещё не отправлено» не должно
      // всплывать наверх очереди только от того, что порядок перевернули.
      const leftEmpty = isEmpty(left);
      const rightEmpty = isEmpty(right);
      if (leftEmpty || rightEmpty) {
        if (leftEmpty && rightEmpty) return 0;
        return leftEmpty ? 1 : -1;
      }
      const result = compare(left as string | number, right as string | number);
      return sort.direction === "asc" ? result : -result;
    });
    return copy;
  }, [items, accessors, sort]);

  return { sorted, sort, toggle, setSort };
}
